/**
 * AdminPushPanel — /admin/growth/push
 *
 * Cockpit for push notification performance. Admin can see which
 * notifications are driving behaviour and which to kill. Directly
 * wired to the backend performance panel endpoint.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Bell, Warning, Fire } from '@phosphor-icons/react';
import EngagementRing from '../components/EngagementRing';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPushPanel = () => {
  const navigate = useNavigate();
  const [window, setWindow] = useState(7);
  const [data, setData] = useState(null);
  const [ranking, setRanking] = useState([]);
  const [error, setError] = useState(null);
  const [toggling, setToggling] = useState(null);

  const load = useCallback(async (w) => {
    setError(null);
    try {
      const r = await axios.get(`${API}/admin/growth/push`, { params: { window_days: w } });
      setData(r.data);
    } catch (e) {
      if (e.response?.status === 401) setError('Please sign in');
      else if (e.response?.status === 403) setError('Admin access required');
      else setError('Could not load push metrics');
    }
    try {
      const r = await axios.get(`${API}/admin/growth/engagement-ranking`,
        { params: { window_days: Math.max(w, 30) } });
      setRanking(r.data?.events || []);
    } catch { setRanking([]); }
  }, []);

  useEffect(() => { load(window); }, [load, window]);

  const toggle = async (type, enabled) => {
    setToggling(type);
    try {
      await axios.put(`${API}/admin/growth/push/toggle`, { type, enabled });
      toast.success(`${type} ${enabled ? 'enabled' : 'disabled'}`);
      await load(window);
    } catch { toast.error('Toggle failed'); }
    finally { setToggling(null); }
  };

  const lockVariant = async (flavour, reason, lockedVariant) => {
    try {
      await axios.post(`${API}/admin/growth/push/variant-lock`, {
        flavour, reason, locked_variant: lockedVariant,
      });
      toast.success(`Locked ${lockedVariant} for ${flavour} · ${reason} (80/20 split)`);
      await load(window);
    } catch (e) {
      const msg = e.response?.data?.detail?.message || 'Could not lock variant';
      toast.error(msg);
    }
  };

  const unlockVariant = async (flavour, reason) => {
    try {
      await axios.delete(`${API}/admin/growth/push/variant-lock`, {
        params: { flavour, reason },
      });
      toast.success(`Unlocked ${flavour} · ${reason} (back to 50/50)`);
      await load(window);
    } catch { toast.error('Unlock failed'); }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#0F1014] flex items-center justify-center text-white" data-testid="push-error">
        <div className="text-center"><Warning size={28} weight="fill" className="text-amber-300 mx-auto mb-3" /><p className="font-black">{error}</p></div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen bg-[#0F1014] flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-4 border-[#B89762]/40 border-t-transparent" /></div>;

  const { overview, types, recent, heatmap } = data;

  return (
    <div className="min-h-screen bg-[#0F1014] text-white" data-testid="admin-push-panel">
      <div className="border-b border-white/5 px-5 py-5" style={{ background: 'linear-gradient(180deg, rgba(108,85,224,0.1), transparent)' }}>
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-3">
          <div>
            <button onClick={() => navigate('/admin/growth')} className="text-white/40 hover:text-white text-xs mb-1 flex items-center gap-1" data-testid="back-btn">
              <ArrowLeft size={12} weight="bold" /> Growth
            </button>
            <h1 className="text-2xl md:text-3xl font-black flex items-center gap-2">
              <Bell size={24} weight="fill" className="text-[#C29D52]" /> Push Performance
            </h1>
            <p className="text-white/50 text-xs mt-0.5">Trigger → Notify → Measure → Kill weak performers, double down on strong ones.</p>
          </div>
          <div className="flex gap-1 p-1 rounded-lg bg-white/[0.05] border border-white/10">
            {[7, 30, 90].map((w) => (
              <button key={w} onClick={() => setWindow(w)} className="px-3 py-1.5 rounded-md text-xs font-bold" style={{ background: window === w ? 'linear-gradient(135deg, #B89762, #C29D52)' : 'transparent', color: window === w ? 'white' : 'rgba(255,255,255,0.5)' }} data-testid={`push-window-${w}`}>
                {w}d
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 py-6 space-y-6">
        {/* Overview strip */}
        <section>
          <h2 className="text-xs font-black tracking-[2px] uppercase text-white/50 mb-3">Push overview</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <OvTile label="Sent"      value={overview.sent.toLocaleString()}           testId="ov-sent" />
            <OvTile label="Delivered" value={`${overview.delivered_pct}%`}              testId="ov-delivered" accent="cyan" />
            <OvTile label="Opened"    value={`${overview.opened_pct}%`}                 testId="ov-opened" />
            <OvTile label="Clicked"   value={`${overview.clicked_pct}%`}                testId="ov-clicked" />
            <OvTile label="Converted" value={`${overview.converted_pct}%`}              testId="ov-converted" accent="emerald" />
          </div>
        </section>

        {/* Per-type table */}
        <section>
          <h2 className="text-xs font-black tracking-[2px] uppercase text-white/50 mb-3">Performance by type</h2>
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
            <div className="hidden md:grid grid-cols-8 gap-2 px-4 py-2.5 text-[10px] font-black uppercase tracking-wider text-white/40 border-b border-white/5">
              <div className="col-span-2">Type</div>
              <div>Sent</div>
              <div>Open</div>
              <div>Click</div>
              <div>Convert</div>
              <div className="col-span-2 text-right">Control</div>
            </div>
            {types.map((t) => {
              const hot = t.converted_pct >= 25;
              const weak = t.sent >= 20 && t.converted_pct < 5;
              return (
                <div key={t.type} className="grid grid-cols-2 md:grid-cols-8 gap-2 px-4 py-3 border-b border-white/5 items-center" data-testid={`push-row-${t.type}`}>
                  <div className="col-span-2">
                    <p className="text-sm font-bold text-white flex items-center gap-1.5">
                      {t.label}
                      {hot && <Fire size={12} weight="fill" className="text-amber-300" />}
                      {weak && <Warning size={12} weight="fill" className="text-red-300" />}
                    </p>
                    <p className="text-[10px] text-white/35 uppercase tracking-wide">Tier {t.tier}</p>
                  </div>
                  <div className="text-sm text-white/80">{t.sent}</div>
                  <div className="text-sm text-white/80">{t.opened_pct}%</div>
                  <div className="text-sm text-white/80">{t.clicked_pct}%</div>
                  <div className={`text-sm font-bold ${hot ? 'text-emerald-300' : weak ? 'text-red-300' : 'text-white/80'}`}>{t.converted_pct}%</div>
                  <div className="col-span-2 text-right">
                    <button
                      onClick={() => toggle(t.type, !t.enabled)}
                      disabled={toggling === t.type}
                      className="px-3 py-1.5 rounded-lg text-[11px] font-bold"
                      style={{
                        background: t.enabled ? 'rgba(16,185,129,0.15)' : 'rgba(255,255,255,0.05)',
                        border: `1px solid ${t.enabled ? 'rgba(16,185,129,0.4)' : 'rgba(255,255,255,0.1)'}`,
                        color: t.enabled ? 'rgb(110, 231, 183)' : 'rgba(255,255,255,0.6)',
                      }}
                      data-testid={`push-toggle-${t.type}`}
                    >
                      {t.enabled ? 'ON' : 'OFF'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Recent sends */}
        {recent.length > 0 && (
          <section>
            <h2 className="text-xs font-black tracking-[2px] uppercase text-white/50 mb-3">Recent sends</h2>
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-2 space-y-1" data-testid="push-recent">
              {recent.map((r, i) => (
                <div key={i} className="px-3 py-2 rounded-lg hover:bg-white/[0.03] flex items-center gap-2 text-[11px]">
                  <span className="text-white/40 shrink-0 w-20">{new Date(r.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>
                  <span className="text-white/70 font-bold shrink-0">{r.type}</span>
                  <span className="text-white/55 truncate">{r.title} · {r.body}</span>
                  <span className="ml-auto text-[10px] font-black uppercase tracking-wider shrink-0" style={{ color: r.transport === 'push' ? 'rgb(103, 232, 249)' : r.transport === 'email' ? 'rgb(251, 191, 36)' : 'rgba(255,255,255,0.3)' }}>
                    {r.transport || 'dropped'}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Top events by engagement */}
        {ranking.length > 0 && (
          <section data-testid="engagement-ranking">
            <h2 className="text-xs font-black tracking-[2px] uppercase text-white/50 mb-1">Top events by engagement</h2>
            <p className="text-white/40 text-[11px] mb-3">Which events are pulling guests back? Amplify the winners.</p>
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] divide-y divide-white/5">
              {ranking.slice(0, 10).map((ev) => (
                <div key={ev.event_id} className="px-4 py-3 flex items-center justify-between gap-4" data-testid={`ranking-row-${ev.event_id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-black text-white truncate">{ev.event_name}</p>
                    <p className="text-[11px] text-white/40">{ev.event_date || '—'} · n={ev.sample_size} · {ev.returned} returned</p>
                  </div>
                  <EngagementRing
                    rate={ev.rate}
                    ratePct={ev.rate_pct}
                    sampleSize={ev.sample_size}
                    tier={ev.tier}
                    variant="admin"
                    size={44}
                    stroke={5}
                    testId={`ranking-ring-${ev.event_id}`}
                  />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Copy variant A/B performance */}
        {data.variant_performance && data.variant_performance.length > 0 && (
          <VariantPerformance
            rows={data.variant_performance}
            thresholds={data.variant_thresholds || { min_sends_per_variant: 50, min_delta: 0.05 }}
            onLock={lockVariant}
            onUnlock={unlockVariant}
          />
        )}

        {/* Conversion Heatmap */}
        {heatmap && (
          <PushHeatmap heatmap={heatmap} dominance={data.flavour_dominance || []} />
        )}
      </div>
    </div>
  );
};

const DOW_LABEL = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const FLAVOUR_COLOR = {
  manual:    '#a855f7',  // violet
  scheduled: '#10b981',  // emerald
  auto:      '#38bdf8',  // sky
};

const PushHeatmap = ({ heatmap, dominance = [] }) => {
  const cells = heatmap.cells || [];
  const byKey = {};
  let maxRate = 0;
  cells.forEach((c) => {
    byKey[`${c.dow}-${c.hour}`] = c;
    if (c.rate > maxRate) maxRate = c.rate;
  });
  const domByKey = {};
  dominance.forEach((d) => { domByKey[`${d.dow}-${d.hour}`] = d; });
  const cellColor = (c) => {
    if (!c || c.sent === 0) return 'rgba(255,255,255,0.03)';
    const rate = c.rate;
    if (rate >= 25) return 'rgba(16,185,129,0.80)'; // peak green
    if (rate >= 15) return 'rgba(16,185,129,0.55)'; // strong
    if (rate >= 5)  return 'rgba(251,191,36,0.55)'; // medium
    return 'rgba(239,68,68,0.55)';                  // weak
  };
  return (
    <section data-testid="push-heatmap">
      <h2 className="text-xs font-black tracking-[2px] uppercase text-white/50 mb-1">Push timing performance</h2>
      <p className="text-white/40 text-[11px] mb-3">{heatmap.note || 'Conversion rate per slot — find the windows that convert best.'}</p>
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-3 overflow-x-auto">
        <div className="min-w-[720px]">
          <div className="grid grid-cols-[48px_repeat(24,1fr)] gap-[3px] mb-[3px] text-[9px] font-bold text-white/35">
            <div />
            {Array.from({ length: 24 }, (_, h) => (
              <div key={h} className="text-center">{h % 3 === 0 ? `${h}` : ''}</div>
            ))}
          </div>
          {DOW_LABEL.map((day, dowIdx) => (
            <div key={day} className="grid grid-cols-[48px_repeat(24,1fr)] gap-[3px] mb-[3px]">
              <div className="text-[10px] font-bold text-white/50 flex items-center">{day}</div>
              {Array.from({ length: 24 }, (_, h) => {
                const c = byKey[`${dowIdx}-${h}`];
                const dom = domByKey[`${dowIdx}-${h}`];
                const isPeak = c && maxRate > 0 && c.rate === maxRate && c.sent >= 2;
                return (
                  <div
                    key={h}
                    className="h-6 rounded-sm relative group overflow-hidden"
                    style={{ background: cellColor(c) }}
                    title={dom
                      ? `${day} ${h}:00 · ${c?.sent || 0} sent · ${c?.rate || 0}% converted · ${dom.winner} wins (${dom.rate_pct}%)`
                      : c ? `${day} ${h}:00 · ${c.sent} sent · ${c.rate}% converted` : `${day} ${h}:00 · no data`}
                    data-testid={`heatmap-cell-${dowIdx}-${h}`}
                  >
                    {dom && (
                      <div
                        className="absolute top-0 left-0 right-0"
                        style={{ height: '3px', background: FLAVOUR_COLOR[dom.winner] || 'transparent' }}
                        data-testid={`heatmap-stripe-${dowIdx}-${h}`}
                      />
                    )}
                    {isPeak && <span className="absolute inset-0 flex items-center justify-center text-[9px]">🔥</span>}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <div className="flex items-center gap-3 mt-3 text-[10px] font-bold text-white/50 flex-wrap">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm inline-block" style={{background:'rgba(239,68,68,0.55)'}} />low</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm inline-block" style={{background:'rgba(251,191,36,0.55)'}} />medium</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm inline-block" style={{background:'rgba(16,185,129,0.55)'}} />strong</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm inline-block" style={{background:'rgba(16,185,129,0.80)'}} />peak 🔥</span>
          <span className="text-white/30 mx-2">·</span>
          <span className="flex items-center gap-1"><span className="w-3 h-1 rounded-sm inline-block" style={{background:FLAVOUR_COLOR.manual}} />manual</span>
          <span className="flex items-center gap-1"><span className="w-3 h-1 rounded-sm inline-block" style={{background:FLAVOUR_COLOR.scheduled}} />scheduled</span>
          <span className="flex items-center gap-1"><span className="w-3 h-1 rounded-sm inline-block" style={{background:FLAVOUR_COLOR.auto}} />auto</span>
        </div>
      </div>
    </section>
  );
};

const OvTile = ({ label, value, accent, testId }) => (
  <div className="rounded-xl p-3 border" style={{
    background: accent === 'emerald' ? 'rgba(16,185,129,0.08)' : accent === 'cyan' ? 'rgba(194,157,82,0.08)' : 'rgba(255,255,255,0.02)',
    borderColor: accent === 'emerald' ? 'rgba(16,185,129,0.3)' : accent === 'cyan' ? 'rgba(194,157,82,0.3)' : 'rgba(255,255,255,0.1)',
  }} data-testid={testId}>
    <p className="text-[10px] font-black tracking-wider uppercase text-white/50">{label}</p>
    <p className="text-2xl font-black mt-1">{value}</p>
  </div>
);

const VariantPerformance = ({ rows, thresholds, onLock, onUnlock }) => {
  const minSends = thresholds.min_sends_per_variant;
  const minDelta = Math.round((thresholds.min_delta || 0) * 100);
  return (
    <section data-testid="variant-performance">
      <h2 className="text-xs font-black tracking-[2px] uppercase text-white/50 mb-1">Copy variant A/B</h2>
      <p className="text-white/40 text-[11px] mb-3">
        Deterministic hash-based assignment per (user × event × flavour × reason).
        Winner declared at ≥{minSends} sends per variant and ≥{minDelta}% delta.
        Locking a winner tilts traffic 80/20 — winner gets the bulk, loser keeps 20% for continued learning.
      </p>
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-2.5 text-[10px] font-black uppercase tracking-wider text-white/40 border-b border-white/5">
          <div className="col-span-3">Flavour · Reason</div>
          <div className="col-span-4">Variant A</div>
          <div className="col-span-4">Variant B</div>
          <div className="col-span-1 text-right">Status</div>
        </div>
        {rows.map((row) => {
          const key = `${row.flavour}-${row.reason}`;
          const a = row.variant_a;
          const b = row.variant_b;
          const winner = row.winner;
          const locked = row.locked_variant;
          const highlightA = locked === 'A' || (!locked && winner === 'A');
          const highlightB = locked === 'B' || (!locked && winner === 'B');
          const flavourColor = FLAVOUR_COLOR[row.flavour] || '#94a3b8';
          return (
            <div key={key}>
              {row.is_stable_winner && (
                <div
                  className="px-4 py-2 border-b border-white/5 flex items-center gap-2 text-[11px]"
                  style={{ background: 'rgba(16,185,129,0.06)' }}
                  data-testid={`variant-suggestion-${key}`}
                >
                  <span className="text-base leading-none">💡</span>
                  <span className="text-emerald-200 font-bold">
                    Suggested: Lock {winner}
                  </span>
                  <span className="text-white/50">
                    — stable winner for {row.stable_days} day{row.stable_days === 1 ? '' : 's'}
                  </span>
                  <button
                    onClick={() => onLock(row.flavour, row.reason, winner)}
                    className="ml-auto px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-wider"
                    style={{ background: 'rgba(16,185,129,0.2)', border: '1px solid rgba(16,185,129,0.5)', color: 'rgb(134, 239, 172)' }}
                    data-testid={`variant-suggest-lock-${key}`}
                  >
                    Lock now
                  </button>
                </div>
              )}
              <div
                className="grid grid-cols-2 md:grid-cols-12 gap-2 px-4 py-3 border-b border-white/5 items-center"
                data-testid={`variant-row-${key}`}
              >
                <div className="col-span-2 md:col-span-3">
                  <p className="text-sm font-bold text-white flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ background: flavourColor }} />
                    <span className="capitalize">{row.flavour}</span>
                    <span className="text-white/40">·</span>
                    <span className="text-white/70 font-normal">{row.reason}</span>
                  </p>
                  {row.daily_performance && row.daily_performance.length >= 2 && (
                    <StabilitySparkline
                      points={row.daily_performance}
                      winner={row.winner}
                      testKey={key}
                    />
                  )}
                </div>
                <VariantCell cell={a} label="A" isHighlight={highlightA} testId={`variant-a-${key}`} />
                <VariantCell cell={b} label="B" isHighlight={highlightB} testId={`variant-b-${key}`} />
                <div className="col-span-2 md:col-span-1 text-right">
                  <VariantStatus
                    row={row}
                    onLock={() => onLock(row.flavour, row.reason, winner)}
                    onUnlock={() => onUnlock(row.flavour, row.reason)}
                    testKey={key}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

const StabilitySparkline = ({ points, winner, testKey }) => {
  // Minimal inline SVG — 100×22 viewport. Two lines (A emerald, B grey).
  // Winning line gets slightly thicker stroke. Tooltip = native title per dot.
  const W = 100, H = 22, PAD = 2;
  const n = points.length;
  if (n < 2) return null;
  // Shared Y scale across both variants so lines are directly comparable
  let max = 0;
  points.forEach((p) => {
    if (p.A > max) max = p.A;
    if (p.B > max) max = p.B;
  });
  if (max === 0) max = 0.01; // avoid divide-by-zero
  const x = (i) => PAD + (i * (W - PAD * 2)) / Math.max(n - 1, 1);
  const y = (v) => H - PAD - (v / max) * (H - PAD * 2);
  const path = (key) => points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)} ${y(p[key]).toFixed(1)}`).join(' ');
  const dayLabel = (iso) => {
    try {
      return new Date(iso + 'T12:00:00Z').toLocaleDateString('en-GB', { weekday: 'short' });
    } catch {
      return iso.slice(5);
    }
  };
  const title = points.map((p) => {
    const aPct = Math.round(p.A * 100);
    const bPct = Math.round(p.B * 100);
    return `${dayLabel(p.day)}: A ${aPct}% · B ${bPct}%`;
  }).join('\n');
  return (
    <div className="mt-1.5 flex items-center gap-1.5" data-testid={`variant-sparkline-${testKey}`}>
      <svg
        width={W} height={H}
        className="overflow-visible"
        aria-label={`Last ${n} day${n === 1 ? '' : 's'} of A vs B conversion`}
      >
        <title>{title}</title>
        <path d={path('B')} fill="none" stroke="rgba(255,255,255,0.35)"
              strokeWidth={winner === 'B' ? 1.6 : 1.1} strokeLinejoin="round" />
        <path d={path('A')} fill="none" stroke="rgb(52, 211, 153)"
              strokeWidth={winner === 'A' ? 1.6 : 1.1} strokeLinejoin="round" />
        {points.map((p, i) => (
          <g key={p.day}>
            <circle cx={x(i)} cy={y(p.A)} r="1.3" fill="rgb(52, 211, 153)" />
            <circle cx={x(i)} cy={y(p.B)} r="1.3" fill="rgba(255,255,255,0.55)" />
          </g>
        ))}
      </svg>
      <span className="text-[9px] text-white/35 font-bold tracking-wider uppercase">{n}d</span>
    </div>
  );
};

const VariantCell = ({ cell, label, isHighlight, testId }) => (
  <div
    className="col-span-1 md:col-span-4 rounded-lg px-3 py-2"
    style={{
      background: isHighlight ? 'rgba(16,185,129,0.08)' : 'rgba(255,255,255,0.02)',
      border: `1px solid ${isHighlight ? 'rgba(16,185,129,0.3)' : 'rgba(255,255,255,0.06)'}`,
    }}
    data-testid={testId}
  >
    <div className="flex items-center gap-2">
      <span className="text-[10px] font-black uppercase tracking-wider text-white/45">{label}</span>
      <span className={`text-base font-black ${isHighlight ? 'text-emerald-300' : 'text-white/85'}`}>
        {cell.conv_pct}%
      </span>
      <span className="ml-auto text-[10px] text-white/40">{cell.sent} sent · {cell.converted} conv</span>
    </div>
  </div>
);

const VariantStatus = ({ row, onLock, onUnlock, testKey }) => {
  if (row.locked_variant) {
    return (
      <div className="flex flex-col items-end gap-1" data-testid={`variant-locked-${testKey}`}>
        <span
          className="px-2 py-1 rounded-md text-[10px] font-black uppercase tracking-wider"
          style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.4)', color: 'rgb(110, 231, 183)' }}
        >
          {row.locked_variant} wins · locked 80%
        </span>
        <button
          onClick={onUnlock}
          className="text-[9px] font-bold uppercase tracking-wider text-white/40 hover:text-white/70"
          data-testid={`variant-unlock-${testKey}`}
        >
          Unlock
        </button>
      </div>
    );
  }
  if (row.confident && row.winner) {
    const stable = row.stability_label === 'stable';
    const tagStyle = stable
      ? { background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.35)', color: 'rgb(110, 231, 183)' }
      : { background: 'rgba(251,191,36,0.10)', border: '1px solid rgba(251,191,36,0.35)', color: 'rgb(252, 211, 77)' };
    const tagText = stable ? `Stable · ${row.stable_days}d` : 'Recent';
    return (
      <div className="flex flex-col items-end gap-1">
        <span
          className="px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-wider"
          style={tagStyle}
          data-testid={`variant-stability-${testKey}`}
        >
          {tagText}
        </span>
        <button
          onClick={onLock}
          className="px-2 py-1 rounded-md text-[10px] font-black uppercase tracking-wider"
          style={{ background: 'rgba(194,157,82,0.15)', border: '1px solid rgba(194,157,82,0.4)', color: 'rgb(103, 232, 249)' }}
          data-testid={`variant-lock-${testKey}`}
        >
          Lock {row.winner}
        </button>
      </div>
    );
  }
  return (
    <span
      className="text-[10px] font-bold uppercase tracking-wider text-white/35"
      data-testid={`variant-learning-${testKey}`}
    >
      Learning
    </span>
  );
};

export default AdminPushPanel;
